import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { CloseButton, SdPanel } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, useSlow, inertClass } from '../../components/pending'
import {
  HOLD_CHIP,
  PSTATE,
  confirmMismatch,
  digits,
  drawerActions,
  facts,
  fmtStamp,
  type PalletDetail,
  type Ref,
} from './model'
import { withCurrentRef } from './loads'
import { NoteBubble } from './NoteBubble'
import { hasCapability, useAccess } from '../../lib/access'

// The pallet drawer (V4 palDrawer): figures, the gates still open before it
// can depart, the actions its state allows, and its history. Every action is
// a POST the API validates again; the drawer only reloads what it returns.

type Panel = null | { kind: 'confirm'; recount: boolean } | { kind: 'move' } | { kind: 'hold' } | { kind: 'note' }

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function PalletDrawer({
  user,
  palletId,
  onClose,
  onChanged,
}: {
  user: any
  palletId: string
  onClose: () => void
  onChanged: (message?: string) => void
}) {
  const [p, setP] = useState<PalletDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const [panel, setPanel] = useState<Panel>(null)
  const [opts, setOpts] = useState<{ lids: Ref[]; locations: Ref[] } | null>(null)
  const [helpOn, toggleHelp] = useHelpPref(user, 'pallet_drawer')
  const mayManage = hasCapability(useAccess(), 'manage_stock_dispatch')

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      setP(await apiJson<PalletDetail>(userRef.current, `/api/pallets/${palletId}`))
      setError(null)
    } catch (e) {
      setError(`Failed to load pallet: ${errText(e)}`)
    }
  }, [palletId])

  useEffect(() => {
    load()
  }, [load])

  const needOpts = panel && panel.kind !== 'hold' && panel.kind !== 'note'
  useEffect(() => {
    if (!needOpts || opts) return
    apiJson<{ lids: Ref[]; locations: Ref[] }>(userRef.current, '/api/pallets/options')
      .then((r) => setOpts({ lids: r.lids, locations: r.locations }))
      .catch((e) => setError(`Failed to load lids and locations: ${errText(e)}`))
  }, [needOpts, opts])

  const act = async (action: string, body: Record<string, unknown> = {}) => {
    setBusyOn(action)
    try {
      const r = await apiJson<{ message: string }>(userRef.current, `/api/pallets/${palletId}/${action}`, { method: 'POST', body })
      setPanel(null)
      setError(null)
      await load()
      onChanged(r.message)
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusyOn(null)
    }
  }

  const a = p ? drawerActions(p) : null
  // Without Manage stock & dispatch the drawer is a record to read: every
  // action below is a POST the API refuses without it.
  const acts = mayManage ? a : null

  // Drawer and panels (h 425-458): the drawer is a layer; the panels over it
  // are guarded layers that ask first if they hold unsaved state.
  const drawerEl = useRef<HTMLDivElement>(null)
  const drawer = useLayer(true, onClose, { el: drawerEl, modal: true })

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && drawer.requestClose()}>
      <div ref={drawerEl} tabIndex={-1} className={`${o.drawer} ${sd.pallet}`} role="dialog" aria-label={p ? `Pallet ${p.pallet_id}` : 'Pallet'}>
        <div className={styles.dHead}>
          <span className={styles.dTitle}>{p?.pallet_id || 'Pallet'}</span>
          {p && (
            <span className={styles.chip} style={{ background: PSTATE[p.state][1], color: PSTATE[p.state][2] }}>
              {PSTATE[p.state][0]}
            </span>
          )}
          <div className={styles.dHeadRight}>
            <button type="button" className={pa.helpBtn} onClick={toggleHelp}>
              {helpOn ? 'Hide help' : 'How pallets work'}
            </button>
            <CloseButton onClick={drawer.requestClose} />
          </div>
        </div>
        <div className={styles.dBody}>
          {error && (
            <div className={styles.alert} role="alert">
              {error}
            </div>
          )}
          {helpOn && (
            <div className={styles.helpBox}>
              <button type="button" className={styles.helpX} title="Hide help" aria-label="Hide help" onClick={toggleHelp}>
                ×
              </button>
              <div className={styles.helpItem}>
                <span>Planned is not confirmed</span>
                <p>
                  A planned pallet earmarks boxes from one stock group. Confirming records what was actually built. If the two differ, the pallet
                  goes to discrepancy and the difference is written back to the group.
                </p>
              </div>
              <div className={styles.helpItem}>
                <span>What has to be true before it can leave</span>
                <p>
                  Confirmed, not on hold, the right lid applied, and an identity label printed. Any gate still open is listed below the figures and
                  blocks the load, not the pallet.
                </p>
              </div>
            </div>
          )}
          {p && a && (
            <>
              {p.hold && (
                <div className={styles.alertStrong}>
                  {HOLD_CHIP[0]}: {p.hold.reason} · {p.hold.by} · {fmtStamp(p.hold.at)}
                </div>
              )}
              {p.note && (
                <div className={styles.note} data-testid="pallet-note">
                  <NoteBubble notes={[{ text: p.note.text }]} />
                  <span>
                    {p.note.text}
                    <span className={styles.noteBy}>
                      {p.note.by}
                      {p.note.at ? ' · ' + fmtStamp(p.note.at) : ''}
                    </span>
                  </span>
                </div>
              )}
              {p.state === 'discrepancy' && (
                <div className={styles.amber}>
                  Confirmed {p.confirmed_boxes} against planned {p.planned_boxes}. Resolve in Control: accept the confirmed count or recount. Nothing on
                  the order or source changes until then.
                </div>
              )}
              {a.confirmLid && (
                <div className={styles.amber}>
                  Lid change outstanding: applied {p.applied_lid?.name || 'none'}, order requires {p.required_lid?.name}.
                </div>
              )}
              <div className={styles.facts}>
                {facts(p).map((f) => (
                  <div key={f.k} className={styles.fact}>
                    <span className={styles.factK}>{f.k}</span>
                    <span>{f.v}</span>
                  </div>
                ))}
              </div>
              {p.gates.length > 0 && (
                <div className={styles.gates} data-testid="pallet-gates">
                  <div className={styles.gatesTitle}>Cannot depart yet</div>
                  {p.gates.map((g) => (
                    <div key={g} className={styles.gate}>
                      • {g}
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.actions}>
                {acts?.confirm && (
                  <button type="button" className={pa.primaryBtn} disabled={busy} onClick={() => setPanel({ kind: 'confirm', recount: false })}>
                    Confirm pallet
                  </button>
                )}
                {acts?.confirmLid && (
                  <PendingButton type="button" className={pa.primaryBtn} busy={busyOn === 'confirm-lid'} disabled={busy || busyOn !== null && busyOn !== 'confirm-lid'} pending="Confirming…" onClick={() => act('confirm-lid')}>
                    Confirm lid change
                  </PendingButton>
                )}
                {acts?.accept && (
                  <>
                    <PendingButton type="button" className={pa.primaryBtn} busy={busyOn === 'accept-count'} disabled={busy || busyOn !== null && busyOn !== 'accept-count'} pending="Accepting…" onClick={() => act('accept-count')}>
                      Accept confirmed count
                    </PendingButton>
                    <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setPanel({ kind: 'confirm', recount: true })}>
                      Recount
                    </button>
                  </>
                )}
                {acts?.move && (
                  <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setPanel({ kind: 'move' })}>
                    Move
                  </button>
                )}
                {acts?.hold && (
                  <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setPanel({ kind: 'hold' })}>
                    Hold
                  </button>
                )}
                {mayManage && !p.departed && !p.cancelled && (
                  <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => setPanel({ kind: 'note' })}>
                    {p.note ? 'Edit note' : 'Add note'}
                  </button>
                )}
                {acts?.release && (
                  <PendingButton type="button" className={pa.ghostBtn} busy={busyOn === 'release'} disabled={busy || busyOn !== null && busyOn !== 'release'} pending="Releasing…" onClick={() => act('release')}>
                    Release hold
                  </PendingButton>
                )}
                {acts?.cancel && (
                  <PendingButton type="button" className={styles.dangerBtn} busy={busyOn === 'cancel'} disabled={busy || busyOn !== null && busyOn !== 'cancel'} pending="Canceling…" onClick={() => act('cancel')}>
                    Cancel plan
                  </PendingButton>
                )}
              </div>
              {a.confirmBlocked && <div className={styles.amberText}>{a.confirmBlocked}</div>}
              {p.history.length > 0 && (
                <>
                  <div className={styles.section}>History</div>
                  <div className={styles.facts}>
                    {p.history.map((h) => (
                      <div key={h.id} className={styles.hist}>
                        <span className={styles.histAt}>{fmtStamp(h.at)}</span>
                        <span>
                          {h.what}
                          <span className={styles.sub}>
                            {' '}
                            · {h.by} · {h.how}
                          </span>
                          {h.reason && <span className={styles.sub}> · {h.reason}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {p && panel?.kind === 'confirm' && (
        <ConfirmPanel
          p={p}
          recount={panel.recount}
          lids={opts?.lids || []}
          locations={opts?.locations || []}
          busy={busy}
          onCancel={() => setPanel(null)}
          onSubmit={(body) => act('confirm', body)}
        />
      )}
      {p && panel?.kind === 'move' && (
        <MovePanel p={p} locations={opts?.locations || []} busy={busy} onCancel={() => setPanel(null)} onSubmit={(id) => act('move', { location_id: id })} />
      )}
      {p && panel?.kind === 'hold' && (
        <HoldPanel p={p} busy={busy} onCancel={() => setPanel(null)} onSubmit={(reason) => act('hold', { reason })} />
      )}
      {p && panel?.kind === 'note' && (
        <NotePanel p={p} busy={busy} onCancel={() => setPanel(null)} onSubmit={(text) => act('note', { text })} />
      )}
      {drawer.confirm}
    </div>
  )
}

function ConfirmPanel({
  p,
  recount,
  lids,
  locations,
  busy,
  onCancel,
  onSubmit,
}: {
  p: PalletDetail
  recount: boolean
  lids: Ref[]
  locations: Ref[]
  busy: boolean
  onCancel: () => void
  onSubmit: (body: { boxes: number; applied_lid_id: string | null; location_id: string | null }) => void
}) {
  const initQty = String(recount && p.confirmed_boxes != null ? p.confirmed_boxes : p.planned_boxes)
  const initLid = p.required_lid?.id || p.applied_lid?.id || ''
  const initLoc = p.location?.id || ''
  const [qty, setQty] = useState(initQty)
  const [lid, setLid] = useState(initLid)
  // Archived options are not offered (§7n.13), except what this pallet
  // already carries, as the API allows.
  const lidChoices = [p.required_lid, p.line_lid, p.applied_lid].reduce((acc: Ref[], r) => withCurrentRef(acc, r), lids)
  const locChoices = withCurrentRef(locations, p.location)
  const [loc, setLoc] = useState(initLoc)
  const q = parseInt(qty, 10) || 0
  const mismatch = confirmMismatch(qty, p.planned_boxes)
  const over = q > p.capacity

  // A layer (h 425-458): dirty when form state changes.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ qty, lid, loc })
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })

  return (
    <SdPanel
      title={`Confirm ${p.pallet_id}`}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton
          type="button"
          className={o.primary}
          busy={busy}
          disabled={!(q > 0 && q <= p.capacity)}
          pending="Recording…"
          onClick={() => onSubmit({ boxes: q, applied_lid_id: lid || null, location_id: loc || null })}
        >
          Record confirmation
        </PendingButton>
      }
    >
      <p className={sd.text}>
        {p.planned_boxes} boxes planned · capacity {p.capacity} · {p.required_lid ? `Required: ${p.required_lid.name}` : 'No required lid on this pallet'}
      </p>
      <div className={sd.grid}>
        <label className={o.field}>
          <span className={o.fieldLabel}>Actual boxes</span>
          <input className={`${o.input} ${sd.num}`} inputMode="numeric" aria-label="Actual boxes" value={qty} onChange={(e) => setQty(digits(e.target.value))} />
        </label>
        <label className={o.field}>
          <span className={o.fieldLabel}>Applied lid</span>
          <select className={o.input} aria-label="Applied lid" value={lid} onChange={(e) => setLid(e.target.value)}>
            <option value="">No lid applied</option>
            {lidChoices.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className={`${o.field} ${sd.full}`}>
          <span className={o.fieldLabel}>Location (optional)</span>
          <select className={o.input} aria-label="Location" value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value="">Location not recorded</option>
            {locChoices.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {mismatch && <div className={styles.errList}>{mismatch}</div>}
      {over && <div className={sd.bad}>Exceeds capacity {p.capacity}.</div>}
      <p className={sd.muted}>Recorded as a manual confirmation in Control by the signed-in user. Crew records the same event as scanned.</p>
    </SdPanel>
  )
}

function MovePanel({
  p,
  locations,
  busy,
  onCancel,
  onSubmit,
}: {
  p: PalletDetail
  locations: Ref[]
  busy: boolean
  onCancel: () => void
  onSubmit: (locationId: string) => void
}) {
  const [loc, setLoc] = useState('')
  const ok = !!loc && loc !== p.location?.id

  // A layer (h 425-458): dirty when location selection changes.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(loc)
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })

  return (
    <SdPanel
      title={`Move ${p.pallet_id}`}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton type="button" className={o.primary} busy={busy} disabled={!ok} pending="Moving…" onClick={() => onSubmit(loc)}>
          Record move
        </PendingButton>
      }
    >
      <p className={sd.text}>Currently: {p.location?.name || 'Not recorded'}</p>
      <select className={o.input} aria-label="New location" value={loc} onChange={(e) => setLoc(e.target.value)}>
        <option value="">Choose a location</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
    </SdPanel>
  )
}

function HoldPanel({ p, busy, onCancel, onSubmit }: { p: PalletDetail; busy: boolean; onCancel: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('')
  const ok = reason.trim().length > 0

  // A layer (h 425-458): dirty when reason is entered.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(reason)
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })

  return (
    <SdPanel
      title={`Hold ${p.pallet_id}`}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <PendingButton type="button" className={o.dangerBtn} busy={busy} disabled={!ok} pending="Holding…" onClick={() => onSubmit(reason.trim())}>
          Hold pallet
        </PendingButton>
      }
    >
      <p className={sd.help}>
        A hold blocks this pallet from being loaded. The boxes stay in stock and keep their order reservation. Nothing is removed. The hold travels
        with the pallet until someone releases it, and where the pallet is assigned to an order that order shows an exception meanwhile.
      </p>
      <label className={o.field}>
        <span className={o.fieldLabel}>Reason (required)</span>
        <input className={o.input} aria-label="Hold reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className={sd.inset}>
        <div>• {p.order ? `${p.order.reference} shows an assigned-pallet exception until released` : 'Boxes on this pallet leave the available count'}</div>
        <div>• The pallet cannot be added to a load or depart</div>
      </div>
    </SdPanel>
  )
}

// The pallet's note (186): anything the packhouse needs to say about it. One
// note, replaced or cleared here; the history keeps every version.
function NotePanel({ p, busy, onCancel, onSubmit }: { p: PalletDetail; busy: boolean; onCancel: () => void; onSubmit: (text: string) => void }) {
  const [text, setText] = useState(p.note?.text || '')
  const t = text.trim()
  const ok = t !== (p.note?.text || '') && t.length <= 500

  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(text)
  const panel = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, modal: true, busy })

  return (
    <SdPanel
      title={`Note on ${p.pallet_id}`}
      onCancel={panel.requestClose}
      confirm={panel.confirm}
      panelEl={panelEl}
      busy={busy}
      foot={
        <>
          {p.note && (
            <PendingButton type="button" className={o.ghost} busy={busy && !t} disabled={busy} pending="Clearing…" onClick={() => onSubmit('')}>
              Clear note
            </PendingButton>
          )}
          <PendingButton type="button" className={o.primary} busy={busy && !!t} disabled={!ok || !t} pending="Saving…" onClick={() => onSubmit(t)}>
            Save note
          </PendingButton>
        </>
      }
    >
      <p className={sd.help}>Shown as a speech bubble on this pallet and on its stock line, until the pallet departs.</p>
      <label className={o.field}>
        <span className={o.fieldLabel}>Note</span>
        <textarea className={styles.noteInput} aria-label="Pallet note" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} autoFocus />
      </label>
      <p className={sd.muted}>{t.length}/500</p>
    </SdPanel>
  )
}
